/**
 * Data behind the "How it was built" and "Browse the code" views: imported
 * chat histories, the public session list behind a share link, and the
 * public read-only workspace files.
 *
 * The routes are documented in `docs/transcript-import-and-public-workspace-2026-09-29.md`
 * (workspace root). Responses are read tolerantly: the payload may arrive bare
 * (`{ sessions }`) or inside the platform's `{ data: … }` envelope.
 *
 * @module
 */

import type { ChatMessage } from '@molecule/app-ai-chat'

/** One session in a list: an imported chat history or the IDE's own conversation. */
export interface TranscriptSummary {
  id: string
  source: 'molecule-ide' | 'import'
  format: string
  harness: string
  title: string
  model?: string
  startedAt?: string
  turns: number
  importedAt?: string
  /** Owner explicitly selected this conversation for public sharing. */
  public?: boolean
}

/** A file an assistant turn wrote. */
export interface SessionFileWrite {
  path: string
  kind: 'create' | 'edit'
  text: string
  complete: boolean
}

/** One turn of a session. */
export interface SessionTurn {
  role: 'user' | 'assistant'
  text: string
  timestamp?: string
  model?: string
  files: SessionFileWrite[]
}

/** A whole session, as the public route returns it. */
export interface PublicSession {
  /** Redacted native messages, when the source is a Molecule conversation. */
  messages?: ChatMessage[]
  id: string
  source: 'molecule-ide' | 'import'
  format: string
  harness: string
  harnessVersion?: string
  model?: string
  startedAt?: string
  turns: SessionTurn[]
}

/** One file of the public workspace. */
export interface PublicFile {
  path: string
  size: number
  binary?: boolean
}

/** Header the share page presents its slug in (mirrors the API's `SHARE_SLUG_HEADER`). */
export const SHARE_SLUG_HEADER = 'X-Share-Slug'

/**
 * Reads a field off a response body that may or may not be wrapped in `{ data }`.
 *
 * @param body - The response body.
 * @param key - The field.
 * @returns The field's value, or undefined.
 */
export function pick<T>(body: unknown, key: string): T | undefined {
  if (!body || typeof body !== 'object') return undefined
  const direct = (body as Record<string, unknown>)[key]
  if (direct !== undefined) return direct as T
  const inner = (body as { data?: unknown }).data
  if (inner && typeof inner === 'object') return (inner as Record<string, unknown>)[key] as T
  return undefined
}

/**
 * A numeric HTTP status off a thrown client error, if any.
 *
 * @param error - The thrown value.
 * @returns The status, or undefined.
 */
export function statusOf(error: unknown): number | undefined {
  const status = (error as { status?: unknown } | null | undefined)?.status
  return typeof status === 'number' ? status : undefined
}

/**
 * The error key a failed request carried in its body, if any.
 *
 * @param error - The thrown value.
 * @returns The `errorKey`, or undefined.
 */
export function errorKeyOf(error: unknown): string | undefined {
  const e = error as { data?: unknown; response?: { data?: unknown } } | null | undefined
  return pick<string>(e?.data, 'errorKey') ?? pick<string>(e?.response?.data, 'errorKey')
}

/** Files at most this large are offered for import (the API's own limit). */
export const MAX_IMPORT_BYTES = 20 * 1024 * 1024

/** File extensions the importer offers in its picker. */
export const IMPORT_ACCEPT = '.jsonl,.json,.md,.markdown,.txt'

/** A node of the file tree built from the flat file list. */
export interface FileTreeNode {
  name: string
  /** Full path; for a folder, its path without a trailing slash. */
  path: string
  kind: 'file' | 'dir'
  size?: number
  children: FileTreeNode[]
}

/**
 * Builds a folder tree from a flat list of paths: folders first, then files,
 * each alphabetical.
 *
 * @param files - The workspace files.
 * @returns The root's children.
 */
export function buildFileTree(files: ReadonlyArray<PublicFile>): FileTreeNode[] {
  const root: FileTreeNode = { name: '', path: '', kind: 'dir', children: [] }
  for (const f of files) {
    const parts = f.path.split('/').filter(Boolean)
    let node = root
    parts.forEach((part, i) => {
      const path = parts.slice(0, i + 1).join('/')
      const last = i === parts.length - 1
      let child = node.children.find((c) => c.name === part && c.kind === (last ? 'file' : 'dir'))
      if (!child) {
        child = last
          ? { name: part, path, kind: 'file', size: f.size, children: [] }
          : { name: part, path, kind: 'dir', children: [] }
        node.children.push(child)
      }
      node = child
    })
  }
  const sort = (n: FileTreeNode): void => {
    n.children.sort((a, b) =>
      a.kind === b.kind ? a.name.localeCompare(b.name) : a.kind === 'dir' ? -1 : 1,
    )
    n.children.forEach(sort)
  }
  sort(root)
  return root.children
}

/**
 * The folders on the way to a path, so opening a file expands its ancestors.
 *
 * @param path - A file path.
 * @returns Each ancestor folder's path, outermost first.
 */
export function ancestorsOf(path: string): string[] {
  const parts = path.split('/').filter(Boolean)
  return parts.slice(0, -1).map((_p, i) => parts.slice(0, i + 1).join('/'))
}

/** URL search parameter holding the open session. */
export const SESSION_PARAM = 'session'
/** URL search parameter holding the open file. */
export const FILE_PARAM = 'file'

/** highlight.js language for a file, by extension; null to show plain text. */
export function languageFor(path: string): string | null {
  const ext = path.toLowerCase().split('.').pop() ?? ''
  const map: Record<string, string> = {
    ts: 'typescript',
    tsx: 'typescript',
    mts: 'typescript',
    cts: 'typescript',
    js: 'javascript',
    jsx: 'javascript',
    mjs: 'javascript',
    cjs: 'javascript',
    json: 'json',
    css: 'css',
    html: 'xml',
    svg: 'xml',
    xml: 'xml',
    md: 'markdown',
    markdown: 'markdown',
    sh: 'bash',
    bash: 'bash',
    yml: 'yaml',
    yaml: 'yaml',
    sql: 'sql',
  }
  return map[ext] ?? null
}
